import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { InfoText } from '@/components/atoms'
import * as React from 'react'
import { SustainabilityFiltersValues } from '@/utils/constants'
import { GHGEmissionsReportSettings } from '@/hooks'
import { GHGEmissionsView } from './GHGEmissionsView'

import { useSustainabilityFiltersGetParams } from '../../hooks/useSustainabilityFiltersGetParams'
import { GHGEmissionsTotalsCardContainer } from './GHGEmissionsTotalsCardContainer'

type GHGEmissionsReportProps = {
    reportSettings: GHGEmissionsReportSettings
    isOpen?: boolean
    calculationMethodOffset?: number
    filters: SustainabilityFiltersValues
}

export const GHGEmissionsReport: React.FC<GHGEmissionsReportProps> = (
    props
) => {
    const {
        calculationMethodOffset,
        reportSettings,
        isOpen = false,
        filters,
    } = props
    const { t } = useTranslation('translations')

    const scope1TotalsTitle = t(
        'sustainability.ghg_emissions_report.totals.scope1Direct.title'
    )
    const scope1TotalsParams = useSustainabilityFiltersGetParams(filters, {
        type: 'emissionTypes',
        scope: 'scope1',
    })
    const scope2LocationTotalsTitle = t(
        'sustainability.ghg_emissions_report.totals.scope2LocationBased.title'
    )
    const scope2LocationTotalsParams = useSustainabilityFiltersGetParams(
        filters,
        {
            type: 'emissionTypes',
            scope: 'scope2',
            scope2type: 'locationBased',
        }
    )

    const scope2MarketTotalsTitle = t(
        'sustainability.ghg_emissions_report.totals.scope2MarketBased.title'
    )

    const scope2MarketTotalsParams = useSustainabilityFiltersGetParams(
        filters,
        {
            type: 'emissionTypes',
            scope: 'scope2',
            scope2type: 'marketBased',
        }
    )

    return (
        <div
            className={classNames(
                'grid w-full grid-cols-[100%] gap-6 p-6',
                reportSettings.showTotals
                    ? 'grid-rows-[auto_auto_1fr]'
                    : 'grid-rows-[auto_1fr]'
            )}
        >
            <div className="flex min-h-8 items-center justify-between">
                <div className="flex items-center">
                    <div
                        className={classNames({
                            'transition-all duration-300 ease-out': isOpen,
                        })}
                        style={{
                            width: `${String(calculationMethodOffset)}px`,
                        }}
                    />
                    <InfoText
                        text={t('common.calculation_info.calendar_month.label')}
                        icon="info"
                        tooltipContent={t(
                            'common.calculation_info.calendar_month.tooltip'
                        )}
                    />
                </div>
            </div>
            {reportSettings.showTotals && (
                <div className="grid gap-6 md:grid-cols-3">
                    {['scope1', 'all'].includes(reportSettings.scope) && (
                        <GHGEmissionsTotalsCardContainer
                            title={scope1TotalsTitle}
                            params={scope1TotalsParams}
                        />
                    )}
                    {['scope2', 'all'].includes(reportSettings.scope) &&
                        ['locationBased', 'all'].includes(
                            reportSettings.scope2type ?? ''
                        ) && (
                            <GHGEmissionsTotalsCardContainer
                                title={scope2LocationTotalsTitle}
                                params={scope2LocationTotalsParams}
                            />
                        )}
                    {['scope2', 'all'].includes(reportSettings.scope) &&
                        ['marketBased', 'all'].includes(
                            reportSettings.scope2type ?? ''
                        ) && (
                            <GHGEmissionsTotalsCardContainer
                                title={scope2MarketTotalsTitle}
                                params={scope2MarketTotalsParams}
                            />
                        )}
                </div>
            )}
            <GHGEmissionsView
                reportSettings={reportSettings}
                filters={filters}
            />
        </div>
    )
}
